import { useState, useCallback, useMemo, useEffect } from 'react';
import '@react-pdf-viewer/core/lib/styles/index.css';
import HeaderCard from '../../../components/Header/HeaderCard';
import EmployeeNav from './EmployeePayslipNav';
import Payslips from './Payslips';
import { apiService } from '../../../components/Config/api.service';
import PayslipHeader from './PayslipHeader';
import { getEmployeesForAction } from './PayslipUtils';
import { 
    FaHistory, 
    FaMailBulk, 
    FaCalculator, 
    FaPrint,     
    FaLock,    
    FaCalendarWeek,
    FaFileExcel,
    FaUserTie
} from 'react-icons/fa';
import BulletinTab from './PayslipBulletin';
import { notify } from '../../../components/Utils/Notifications';
import PayslipConfirmModal from './PayslipConfirmModal';
import PayslipProgressModal from './PayslipProgressModal';
import AnnualPayslipView from './AnnualPayslipView'; 
import AccountingJournal from '../Compta/AccountingJournal';
import Comptability from '../Compta/Comptability';
import ExternalPayslipsMonthly from './ExternalPayslipsMonthly'; // Add this import

const getLastDayOfMonth = (yearMonth) => {
    const [year, month] = yearMonth.split('-');
    return new Date(year, month, 0).toISOString().split('T')[0];
};

const formatMonthYear = (yearMonth) => {
    const [year, month] = yearMonth.split('-');
    const date = new Date(year, month - 1);
    return date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
};

const pdfBlobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
};

const sendPayslipEmail = async ({ to, subject, html, template_id, params, filename, base64content }) => {
    await apiService.sendEmail({
        to,
        subject,
        html,
        template_id,
        params,
        attachment: {
            filename,
            content: base64content,
        }
    });
};

function PayslipsReport() {
    const [activeTab, setActiveTab] = useState("Bulletin");
    const [activeView, setActiveView] = useState("monthly"); // 'monthly' or 'annual'
    const [pdfUrl, setPdfUrl] = useState('');
    const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [report, setReport] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [employeeId, setEmployeeId] = useState('');
    const [contractId, setContractId] = useState('');
    const [filteredEmployees, setFilteredEmployees] = useState([]);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    const [progress, setProgress] = useState({ current: 0, total: 0 });
    const [modalSelectedEmployees, setModalSelectedEmployees] = useState([]);
    const [isMonthClosed, setIsMonthClosed] = useState(false);
    const [isJournalConfirmOpen, setIsJournalConfirmOpen] = useState(false);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    const refreshEmployeeList = () => setRefreshTrigger(t => t + 1);

    const handleMonthChange = useCallback((e) => setSelectedMonth(e.target.value), []);
    const handleYearChange = useCallback((e) => setSelectedYear(parseInt(e.target.value, 10)), []);

    const handlePdfLoad = useCallback((url) => {
        setPdfUrl(url);
        setIsLoading(false);
    }, []);

    // SUPPRIMER CETTE FONCTION, ELLE N'EST PLUS NÉCESSAIRE
    // const findPayslipForEmployees = async (employeesList, month = selectedMonth) => { ... };
    
    const handleCalculate = useCallback(() => {
        if (!employeeId) {
            alert("Veuillez sélectionner un employé");
            return;
        }
        if (!contractId) {
            alert("Le contrat de l'employé est introuvable. Impossible de calculer le bulletin.");
            return;
        }
        setIsLoading(true);
        setPdfUrl('');
        const newPayslip = {
            employee_id: employeeId,
            contract_id: contractId,
            date_from: `${selectedMonth}-01`,
            date_to: getLastDayOfMonth(selectedMonth),
            name: `Bulletin de ${report?.Employee || "l'employé"} - ${formatMonthYear(selectedMonth)}`,
            state: 'verify'
        };
        console.log('Nouveau bulletin à créer:', newPayslip);
        apiService.saveElement('/payslip', newPayslip)
            .then(response => {
                const payslipId = response.data.result.payslip_id;
                setReport(prev => ({
                    ...prev,
                    id: payslipId,
                    State: 'verify'
                }));
                console.log('Bulletin créé avec succès:', payslipId);
                return apiService.getBlobFile(`/payslip/pdf/${payslipId}`);
            })
            .then(response => {
                if (response && response.data) {
                    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                    setPdfUrl(url);
                    // La mise à jour de l'état se fera au prochain rechargement de la liste
                }
                refreshEmployeeList();
            })
            .catch(error => {
                console.error('Erreur lors du calcul du bulletin:', error);
                alert("Erreur lors du calcul du bulletin");
            })
            .finally(() => setIsLoading(false));
    }, [employeeId, contractId, selectedMonth, report]);

    const handleRecalculate = useCallback(() => {
        if (!report || !report.id) {
            alert("Aucun bulletin à recalculer");
            return;
        }
        setIsLoading(true);
        apiService.updateElementById('/payslip', report.id, { state: 'verify' })
            .then(() => apiService.getBlobFile(`/payslip/pdf/${report.id}`))
            .then(response => {
                if (response && response.data) {
                    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                    setPdfUrl(url);
                    // La mise à jour de l'état se fera au prochain rechargement
                    setReport(prev => ({ ...prev, State: 'verify' }));
                }
                refreshEmployeeList();
            })
            .catch(error => {
                console.error('Erreur lors du recalcul du bulletin:', error);
                alert("Erreur lors du recalcul du bulletin");
            })
            .finally(() => setIsLoading(false));
    }, [report]);

    const handleCalculateAllFilteredPayslips = async (employees, month) => {
        const toProcess = employees.filter(emp => emp.payslip_state !== 'done' && emp.payslip_state !== 'cancel');
        
        if (toProcess.length === 0) {
            notify("Aucun bulletin à calculer.", "top-right", 2);
            return;
        }

        setIsLoading(true);
        setProgress({ current: 0, total: toProcess.length });
        let done = 0;

        for (const emp of toProcess) {
            try {
                if (emp.payslip_id) {
                    // Recalculer un bulletin existant
                    await apiService.updateElementById('/payslip', emp.payslip_id, { state: 'verify' });
                } else {
                    // Créer un nouveau bulletin
                    const newPayslip = {
                        employee_id: emp.id,
                        contract_id: emp.contract_id,
                        date_from: `${month}-01`,
                        date_to: getLastDayOfMonth(month),
                        name: `Bulletin de ${emp.name || "l'employé"} - ${formatMonthYear(month)}`,
                        state: 'verify'
                    };
                    await apiService.saveElement('/payslip', newPayslip);
                }
            } catch (err) {
                console.error(`Erreur lors du calcul du bulletin pour ${emp.name}:`, err);
            }
            done++;
            setProgress(prev => ({ ...prev, current: done }));
        }
        
        notify(`Calcul terminé pour ${done} employés`, "top-right", 2);
        setIsLoading(false);
        setProgress({ current: 0, total: 0 });
        refreshEmployeeList();
    };        

    const handleCancelPayslip = useCallback(() => {
        if (!report || !report.id) {
            alert("Aucun bulletin à annuler");
            return;
        }
        setIsLoading(true);
        apiService.updateElementById('/payslip', report.id, { state: 'cancel' })
            .then(() => {
                notify("Bulletin annulé avec succès", "top-right", 2);
                setReport(prev => ({ ...prev, State: 'cancel' }));
                refreshEmployeeList();
            })
            .catch(error => {
                console.error('Erreur lors de l\'annulation du bulletin:', error);
                notify("Erreur lors de l'annulation du bulletin", "top-right", 3);
            })
            .finally(() => setIsLoading(false));
    }, [report]);

    const handlePrintAllFilteredPayslips = async (employees, month) => {
        setIsLoading(true);
        try {
            const payslipIds = employees
                .map(emp => emp.payslip_id)
                .filter(id => !!id);

            if (payslipIds.length === 0) {
                notify("Aucun bulletin à imprimer", "top-right", 3);
                setIsLoading(false);
                return;
            }

            const pdfResp = await apiService.getBlobFile(`/payslip/pdf?ids=${payslipIds.join(',')}`);
            if (pdfResp?.data) {
                const url = window.URL.createObjectURL(new Blob([pdfResp.data], { type: 'application/pdf' }));
                window.open(url, '_blank');
                notify(`PDF unique généré pour impression (${payslipIds.length} bulletins)`, "top-right", 2);
            } else {
                notify("Erreur lors de la génération du PDF", "top-right", 3);
            }
        } catch (e) {
            notify("Erreur lors de l'impression groupée", "top-right", 3);
            console.error(e);
        }
        setIsLoading(false);
    };

    const handleSendPayslip = useCallback(async () => {
        setIsLoading(true);
        setProgress({ current: 0, total: 1 });
        if (!report || !report.email) {
            alert("Aucun email trouvé pour cet employé.");
            return;
        }
        if (!pdfUrl) {
            alert("Aucun bulletin à envoyer.");
            return;
        }
        setIsLoading(true);
        try {
            const response = await fetch(pdfUrl);
            const blob = await response.blob();
            const base64data = await pdfBlobToBase64(blob);
            await sendPayslipEmail({
                to: report.email,
                subject: `Votre bulletin - ${formatMonthYear(selectedMonth)}`,
                html: `<p>Bonjour,</p><p>Veuillez trouver votre bulletin de paie en pièce jointe.</p>`,
                template_id: 1,
                params: {
                    prenom: report.Employee || "l'employé",
                    month: formatMonthYear(selectedMonth),
                },
                filename: `bulletin-${report.Employee || "employe"}.pdf`,
                base64content: base64data,
            });
            notify("Bulletin envoyé avec succès", "top-right", 2);
            setProgress({ current: 1, total: 1 });
        } catch (e) {
            console.error(e);
            notify("Erreur lors de l'envoi du bulletin", "top-right", 3);
        }
        setIsLoading(false);
        setProgress({ current: 0, total: 0 });
    }, [pdfUrl, report, selectedMonth]);

    const handleSendAllFilteredPayslips = async (employees, month) => {
        setIsLoading(true);
        setProgress({ current: 0, total: employees.length });
        let sent = 0;

        for (const employee of employees) {
            if (!employee.payslip_id) {
                sent++;
                setProgress(prev => ({ ...prev, current: sent }));
                continue;
            }
            try {
                const pdfResp = await apiService.getBlobFile(`/payslip/pdf/${employee.payslip_id}`);
                if (!pdfResp?.data) {
                    sent++;
                    setProgress(prev => ({ ...prev, current: sent }));
                    continue;
                }
                const blob = new Blob([pdfResp.data], { type: 'application/pdf' });
                const base64data = await pdfBlobToBase64(blob);
                await sendPayslipEmail({
                    to: employee.email, // Assurez-vous que l'email est dans l'objet employé
                    subject: `Votre bulletin - ${formatMonthYear(month)}`,
                    html: `<p>Bonjour,</p><p>Merci de trouver votre bulletin de paie en pièce jointe.</p>`,
                    template_id: 1,
                    params: {
                        prenom: employee.name || "l'employé",
                        month: formatMonthYear(month),
                    },
                    filename: `bulletin-${employee.name || "employe"}.pdf`,
                    base64content: base64data,
                });
            } catch (error) {
                console.error(`Erreur lors de l'envoi du bulletin pour ${employee.name}:`, error);
            }
            sent++;
            setProgress(prev => ({ ...prev, current: sent }));
        }
        notify(`Envoi terminé pour ${employees.length} employés`, "top-right", 2);
        setIsLoading(false);
        setProgress({ current: 0, total: 0 });
    };

    const handleConfirm = () => {
        setShowConfirmModal(false);
        if (confirmAction === 'send') {
            handleSendAllFilteredPayslips(modalSelectedEmployees, selectedMonth);
        } else if (confirmAction === 'calculate') {
            handleCalculateAllFilteredPayslips(modalSelectedEmployees, selectedMonth);
        } else if (confirmAction === 'print') {
            handlePrintAllFilteredPayslips(modalSelectedEmployees, selectedMonth);
        } else if (confirmAction === 'close_month') {
            handleCloseMonth();
        }
        setConfirmAction(null);
    };

    const dropdownActions = useMemo(() => {
        const actions = [
            {
                label: 'Vue Annuelle',
                onClick: () => setActiveView(v => v === 'monthly' ? 'annual' : 'monthly'),
                icon: <FaCalendarWeek size={16} />,
                isActive: activeView === 'annual'
            },
            {
                label: 'Historique des bulletins',
                onClick: () => setActiveTab("Historique"),
                icon: <FaHistory size={16} />
            },
            {
                label: 'Relevé Prestataire / Stagiaire',
                onClick: () => setActiveView('external_contracts'),
                icon: <FaUserTie size={16} />,
                isActive: activeView === 'external_contracts'
            },
            {
                isSeparator: true
            },
        ];

        if (isMonthClosed) {
            actions.push({
                color: 'bg-[--color-primary] text-white hover:text-[--color-primary]',
                label: 'Journal comptable',
                // onClick: () => setIsJournalConfirmOpen(true),
                onClick: () => setActiveView('journal'),
                icon: <FaFileExcel size={16} />
            });
        } else {
            actions.push({
                label: 'Calculer les bulletins',
                onClick: () => { 
                    setModalSelectedEmployees(filteredEmployees);
                    setConfirmAction('calculate');
                    setShowConfirmModal(true);
                },
                icon: <FaCalculator size={16} />,
                disabled: isMonthClosed,
            });
        }

        actions.push(
            {
                label: 'Imprimer les bulletins',
                onClick: () => { 
                    setModalSelectedEmployees(filteredEmployees);
                    setConfirmAction('print'); 
                    setShowConfirmModal(true); 
                },
                icon: <FaPrint size={16} />
            },
            {
                label: 'Envoyer les bulletins',
                onClick: () => { 
                    setModalSelectedEmployees(filteredEmployees);
                    setConfirmAction('send'); 
                    setShowConfirmModal(true); 
                },
                icon: <FaMailBulk size={16} />
            }
        );

        return actions;

    }, [filteredEmployees, activeView, isMonthClosed, setActiveTab]);

    const getConfirmModalTitle = () => {
        switch (confirmAction) {
            case 'send': return "Envoi groupé des bulletins";
            case 'calculate': return "Calcul groupé des bulletins";
            case 'print': return "Impression groupée des bulletins";
            case 'close_month': return "Clôturer le mois";
            default: return "";
        }
    };

    const getConfirmModalSubtitle = () => {
        switch (confirmAction) {
            case 'send': return "Envoyer les bulletins par email aux employés sélectionnés";
            case 'calculate': return "Calculer ou recalculer les bulletins pour les employés sélectionnés";
            case 'print': return "Générer un PDF groupé pour impression des bulletins sélectionnés";
            case 'close_month': return "Attention : une fois le mois clôturé, les bulletins ne pourront plus être modifiés.";
            default: return "";
        }
    };

    const getConfirmModalIcon = () => {
        switch (confirmAction) {
            case 'send': return <FaMailBulk className="text-[--color-primary]" size={20} />;
            case 'calculate': return <FaCalculator className="text-[--color-primary]" size={20} />;
            case 'print': return <FaPrint className="text-[--color-primary]" size={20} />;
            case 'close_month': return <FaLock className="text-[--color-primary]" size={20} />;
            default: return null;
        }
    };

        
    const handleCloseMonth = async () => {
        setIsLoading(true);
        try {
            const verifyPayslipIds = modalSelectedEmployees
                .filter(emp => emp.payslip_state === 'verify' && emp.payslip_id)
                .map(emp => emp.payslip_id);

            if (verifyPayslipIds.length === 0) {
                notify("Aucun bulletin à clôturer pour ce mois", "top-right", 2);
                setIsLoading(false);
                return;
            }
            
            await apiService.saveElement('/payslip/close_month', { payslip_ids: verifyPayslipIds });

            notify(`Clôture réussie pour ${verifyPayslipIds.length} bulletins`, "top-right", 2);
            refreshEmployeeList();
        } catch (e) {
            notify("Erreur lors de la clôture du mois", "top-right", 3);
            console.error(e);
        }
        setIsLoading(false);
    };
    
    // SUPPRIMER CE USEEFFECT, L'INFO VIENT MAINTENANT DE EMPLOYEENAV
    // useEffect(() => {
    //     const fetchMonthStatus = async () => {
    //         try {
    //             const res = await apiService.getElement(`/payslip/month_status/${selectedMonth}`);
    //             setIsMonthClosed(res.data.is_closed);                
    //         } catch (e) {
    //             setIsMonthClosed(false);
    //         }
    //     };
    //     fetchMonthStatus();
    // }, [selectedMonth]);

    // Filtrer les employés en attente si action close_month ou calculate
    const employeesToShow = useMemo(
        () => getEmployeesForAction(confirmAction, filteredEmployees), // On retire le 3ème argument obsolète
        [confirmAction, filteredEmployees]
    );

    const selectedCount = modalSelectedEmployees.filter(emp => employeesToShow.some(e => e.id === emp.id)).length;
    const totalCount = employeesToShow.length;

    return (
        <div className="">
            {/* Modal de confirmation avec le composant PayslipConfirmModal */}
            <PayslipConfirmModal
                isOpen={showConfirmModal}
                onClose={() => { setShowConfirmModal(false); setConfirmAction(null); }}
                title={getConfirmModalTitle()}
                subtitle={getConfirmModalSubtitle()}
                headerIcon={getConfirmModalIcon()}
                employeesToShow={employeesToShow}
                modalSelectedEmployees={modalSelectedEmployees}
                setModalSelectedEmployees={setModalSelectedEmployees}
                selectedCount={selectedCount}
                totalCount={totalCount}
                handleConfirm={handleConfirm}
            />

            {/* Modal de progression avec le composant Modal */}
            <PayslipProgressModal progress={progress} />

            {/* Modals for Journal Comptable */}
            <AccountingJournal
                isOpen={isJournalConfirmOpen}
                onClose={() => setIsJournalConfirmOpen(false)}
                selectedMonth={selectedMonth}
            />

            {/* Header principal */}
            <div className="flex justify-between items-start gap-6">
                { activeTab === "Bulletin" && activeView === "annual" ? (
                    <HeaderCard
                        title="Vue annuelle des Bulletins de paie"
                        text="Gérez et générez les bulletins de paie de votre entreprise en toute simplicité."
                    />
                ) : activeTab === "Bulletin" && activeView === "journal" ? (
                    <HeaderCard
                        title="Journal Comptable des Bulletins de paie"
                        text="Gérez et générez les bulletins de paie de votre entreprise en toute simplicité."
                    />
                ) : activeTab === "Bulletin" && activeView === "external_contracts" ? (
                    <HeaderCard
                        title="Relevé Mensuel Prestataires / Stagiaires"
                        text="Consultez et gérez les relevés mensuels des prestataires et stagiaires."
                    />
                ) : activeTab === "Bulletin" && (
                    <HeaderCard
                        title="Bulletins de paie"
                        text="Gérez et générez les bulletins de paie de votre entreprise en toute simplicité."
                    />
                )}
                {activeTab === 'Bulletin' && (
                    <div className="flex gap-4 items-center">
                        <PayslipHeader
                            selectedMonth={selectedMonth}
                            handleMonthChange={handleMonthChange}
                            selectedYear={selectedYear}
                            handleYearChange={handleYearChange}
                            isLoading={isLoading}
                            filteredEmployees={filteredEmployees}
                            onCloseMonth={() => {
                                const employeesToClose = filteredEmployees.filter(emp => emp.payslip_state === 'verify');
                                setConfirmAction('close_month');
                                setShowConfirmModal(true);
                                setModalSelectedEmployees(employeesToClose);
                            }}
                            dropdownActions={dropdownActions}
                            isMonthClosed={isMonthClosed} // Ajouté pour passer le statut
                            activeView={activeView}
                            setActiveView={setActiveView}
                        />
                    </div>
                )}
            </div>

            {/* Contenu principal */}
            {activeView === 'annual' && activeTab === 'Bulletin' ? (
                <AnnualPayslipView year={selectedYear} />
            ) : activeView === 'journal' ? (
                <Comptability 
                    month={selectedMonth}
                    year={selectedYear}
                    setIsJournalConfirmOpen={setIsJournalConfirmOpen}
                />
            ) : activeView === 'external_contracts' ? (
                <ExternalPayslipsMonthly selectedMonth={selectedMonth} />
            ) : (
                <div className="flex gap-4">
                    {activeTab === "Bulletin" && (
                        <div className="w-80 flex-shrink-0">
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                                <div className="px-6 py-4 border-b border-gray-200">
                                    <div className="flex gap-3">                                    
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-800">Collaborateurs</h3>
                                            <p className="text-sm text-gray-600">Sélectionnez un employé</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="p-2">
                                    <EmployeeNav
                                        setPdfUrl={handlePdfLoad}
                                        setIsloading={setIsLoading}
                                        setReport={setReport}
                                        selectedMonth={selectedMonth}
                                        setEmployeeId={setEmployeeId}
                                        setContractId={setContractId}
                                        setFilteredEmployees={setFilteredEmployees}
                                        setIsMonthClosed={setIsMonthClosed}
                                        refreshTrigger={refreshTrigger}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                    
                    <div className={`${activeTab === "Bulletin" ? "flex-1" : "w-full"}`}>
                        <div className="">
                            <div className="">
                                {activeTab === "Bulletin" ? (
                                    <BulletinTab
                                        Report={report}
                                        isLoading={isLoading}
                                        pdfUrl={pdfUrl}
                                        handleCalculate={handleCalculate}
                                        handleRecalculate={handleRecalculate}
                                        handleSendPayslip={handleSendPayslip}
                                        employeeId={employeeId}
                                        handleCancelPayslip={handleCancelPayslip}
                                        onPdfLoaded={() => setIsLoading(false)}
                                    />
                                ) : (
                                    <Payslips
                                        employeeId={employeeId}
                                        setActiveTab={setActiveTab}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default PayslipsReport;